import { useCallback, useRef, useState } from 'react';
import type { FileTreeEntry } from '@localagent/ui';

export interface WorkspaceNodeActionsFileBrowser {
  openPaths: string[];
  refreshTree: (opts?: { manual?: boolean }) => Promise<void>;
  loadChildren: (path: string) => Promise<FileTreeEntry[]>;
  createFile: (path: string, initialContent?: string) => Promise<void>;
  deleteFile: (path: string) => Promise<{ deleted: boolean }>;
  moveFile: (path: string, destination: string) => Promise<{ moved: boolean }>;
  createFolder: (path: string) => Promise<{ version: string }>;
  holdPaths: (prefix: string) => Promise<void>;
  releasePaths: (prefix: string) => void;
  followMovedPath: (from: string, to: string) => void;
  markDeleted: (prefix: string) => void;
}

interface MenuPosition {
  x: number;
  y: number;
}

interface WorkspaceNodeMenu {
  entry: FileTreeEntry | null;
  x: number;
  y: number;
}

export type WorkspaceNameRequest =
  | { kind: 'rename'; entry: FileTreeEntry }
  | { kind: 'new-file'; dirPath: string }
  | { kind: 'new-folder'; dirPath: string };

type WorkspaceConfirmRequest =
  | { kind: 'delete'; entry: FileTreeEntry }
  | { kind: 'move'; entry: FileTreeEntry };

export function parentPathOf(path: string): string {
  const idx = path.lastIndexOf('/');
  return idx === -1 ? '' : path.slice(0, idx);
}

function childPath(dirPath: string, name: string): string {
  return dirPath === '' ? name : `${dirPath}/${name}`;
}

// Delete, rename and move hold the affected paths' saves (app-shell ADR-530ad).
export function useWorkspaceNodeActions({
  fb,
  onOpenFile,
}: {
  fb: WorkspaceNodeActionsFileBrowser;
  onOpenFile: (path: string) => void;
}) {
  const [menu, setMenu] = useState<WorkspaceNodeMenu | null>(null);
  const [nameRequest, setNameRequest] = useState<WorkspaceNameRequest | null>(null);
  const [confirmRequest, setConfirmRequest] = useState<WorkspaceConfirmRequest | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const focusOriginRef = useRef<HTMLElement | null>(null);
  const promptOpeningRef = useRef(false);

  const openMenu = useCallback((entry: FileTreeEntry | null, pos: MenuPosition) => {
    setActionError(null);
    const active = document.activeElement;
    focusOriginRef.current = active instanceof HTMLElement && active !== document.body ? active : null;
    promptOpeningRef.current = false;
    setMenu({ entry, x: pos.x, y: pos.y });
  }, []);

  const openAt = useCallback(
    (entry: FileTreeEntry, pos: MenuPosition) => openMenu(entry, pos),
    [openMenu]
  );

  const openRootAt = useCallback((pos: MenuPosition) => openMenu(null, pos), [openMenu]);

  const closeMenu = useCallback(() => {
    setMenu(null);
    const origin = focusOriginRef.current;
    if (!promptOpeningRef.current && origin !== null && origin.isConnected) origin.focus();
  }, []);

  const startPrompt = useCallback(() => {
    promptOpeningRef.current = true;
    setMenu(null);
  }, []);

  const restoreFocus = useCallback(() => {
    const origin = focusOriginRef.current;
    if (origin !== null && origin.isConnected) origin.focus();
  }, []);
  const closeNameRequest = useCallback(() => setNameRequest(null), []);
  const closeConfirmRequest = useCallback(() => setConfirmRequest(null), []);

  const open = useCallback(
    (entry: FileTreeEntry) => {
      onOpenFile(entry.path);
      setMenu(null);
    },
    [onOpenFile]
  );

  const copyPath = useCallback((entry: FileTreeEntry) => {
    setMenu(null);
    if (navigator.clipboard === undefined) {
      setActionError('Copy path needs clipboard access, which this page does not have (it is not a secure origin).');
      return;
    }
    navigator.clipboard.writeText(entry.path).catch((e: unknown) => {
      setActionError(e instanceof Error ? e.message : String(e));
    });
  }, []);

  const requestRename = useCallback(
    (entry: FileTreeEntry) => {
      startPrompt();
      setNameRequest({ kind: 'rename', entry });
    },
    [startPrompt]
  );

  const requestMove = useCallback(
    (entry: FileTreeEntry) => {
      startPrompt();
      setConfirmRequest({ kind: 'move', entry });
    },
    [startPrompt]
  );

  const requestDelete = useCallback(
    (entry: FileTreeEntry) => {
      startPrompt();
      setConfirmRequest({ kind: 'delete', entry });
    },
    [startPrompt]
  );

  const requestNewFile = useCallback(
    (dirPath: string) => {
      startPrompt();
      setNameRequest({ kind: 'new-file', dirPath });
    },
    [startPrompt]
  );

  const requestNewFolder = useCallback(
    (dirPath: string) => {
      startPrompt();
      setNameRequest({ kind: 'new-folder', dirPath });
    },
    [startPrompt]
  );

  const runPending = useCallback(async (op: () => Promise<void>): Promise<void> => {
    setPending(true);
    try {
      await op();
    } finally {
      setPending(false);
    }
  }, []);

  const moveHeld = useCallback(
    async (from: string, to: string): Promise<void> => {
      const blocking = fb.openPaths.find((p) => p === to || p.startsWith(`${to}/`));
      if (blocking !== undefined) {
        throw new Error(`${blocking} is still open in a tab; close it before moving onto ${to}`);
      }
      await fb.holdPaths(from);
      try {
        await fb.moveFile(from, to);
        fb.followMovedPath(from, to);
      } finally {
        fb.releasePaths(from);
      }
      void fb.loadChildren(parentPathOf(to)).then(
        () => fb.refreshTree(),
        (e: unknown) => {
          setActionError(e instanceof Error ? e.message : String(e));
          return fb.refreshTree();
        }
      );
    },
    [fb]
  );

  const confirmName = useCallback(
    async (name: string): Promise<void> => {
      const request = nameRequest;
      if (request === null) throw new Error('confirmName: no name prompt is open');
      await runPending(async () => {
        if (request.kind === 'rename') {
          await moveHeld(request.entry.path, childPath(parentPathOf(request.entry.path), name));
        } else if (request.kind === 'new-file') {
          await fb.createFile(childPath(request.dirPath, name), '');
        } else {
          const folder = childPath(request.dirPath, name);
          const siblings = await fb.loadChildren(request.dirPath);
          if (siblings.some((e) => e.path === folder)) throw new Error(`${folder} already exists`);
          await fb.createFolder(folder);
          await fb.refreshTree();
        }
      });
      setNameRequest(null);
    },
    [nameRequest, fb, runPending, moveHeld]
  );

  const confirmDelete = useCallback(async (): Promise<void> => {
    const request = confirmRequest;
    if (request === null || request.kind !== 'delete') throw new Error('confirmDelete: no delete prompt is open');
    const path = request.entry.path;
    await runPending(async () => {
      await fb.holdPaths(path);
      try {
        await fb.deleteFile(path);
        fb.markDeleted(path);
      } finally {
        fb.releasePaths(path);
      }
      void fb.refreshTree();
    });
    setConfirmRequest(null);
  }, [confirmRequest, fb, runPending]);

  const confirmMove = useCallback(
    async (destDir: string): Promise<void> => {
      const request = confirmRequest;
      if (request === null || request.kind !== 'move') throw new Error('confirmMove: no move prompt is open');
      await runPending(() => moveHeld(request.entry.path, childPath(destDir, request.entry.name)));
      setConfirmRequest(null);
    },
    [confirmRequest, runPending, moveHeld]
  );

  return {
    menu,
    nameRequest,
    confirmRequest,
    actionError,
    pending,
    openAt,
    openRootAt,
    closeMenu,
    open,
    copyPath,
    requestRename,
    requestMove,
    requestDelete,
    requestNewFile,
    requestNewFolder,
    confirmName,
    confirmDelete,
    confirmMove,
    closeNameRequest,
    closeConfirmRequest,
    restoreFocus,
    loadChildren: fb.loadChildren,
  };
}

export type WorkspaceNodeActions = ReturnType<typeof useWorkspaceNodeActions>;
